import React, { useEffect, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetClarityTags, useSegmentAnalytics } from '@helloworld1812/ws-biz-hooks';
import { useUsersMeSuspense } from '@helloworld1812/hr-api';
import moment from 'moment';
import { GrowthBookProvider } from '@growthbook/growthbook-react';

import { DEPLOY_ENV } from '@/config';
import { useAppDispatch, useAppSelector } from '@/hooks/models';
import { extractSearchParam } from '@/utils/url';
import { FullHeightWrapperWithLoader } from '@/components/common/full-height-wrapper-with-loader';

import { S } from './app.styled';
import { AuthorizedRedirects } from './authorized-redirects';

const AsyncTasksWidget = React.lazy(() => import('@/components/async-tasks-widget'));

interface AuthorizedAppProps {
  children: React.ReactNode;
  noPaddingRoutes?: string[];
}
export function AuthorizedApp(props: AuthorizedAppProps) {
  const { user } = useUsersMeSuspense();
  const { analytics } = useSegmentAnalytics();
  const { company } = user;
  const { function_modules: functionModules } = company;
  const dispatch = useAppDispatch();
  const { children, noPaddingRoutes = [] } = props;
  const app = useAppSelector((state) => state.app);
  useSetClarityTags();
  const location = useLocation();
  const { pathname } = location;

  const isNoPaddingRoute = noPaddingRoutes.includes(pathname);

  const { permittedLocations } = app;

  const setupComplete = !!(
    user &&
    permittedLocations &&
    company &&
    app.textToApplyPhoneNumbers &&
    app.sharePositionSettings
  );

  useEffect(() => {
    if (setupComplete) {
      const bentoSettings = {
        appId: '4b60bce3-d461-46f6-9b07-494c0ab08c78',
        account: {
          id: company.id.toString(),
          name: company.name,
          createdAt: company.onboarding_activated_at,
          locationCount: company.brands
            .filter((brand) => brand.status === 'active')
            .reduce((sum, brand) => sum + brand.location_counts, 0),
          hasOnboarding: functionModules.includes('onboarding'),

          env: DEPLOY_ENV,
        },
        accountUser: {
          id: user.id.toString(),
          fullName: user.name,
          email: user.email,
          role: user.company_role.name,
          createdAt: user.created_at,
          permittedLocationCount: permittedLocations.length,
        },
      };

      window.bentoSettings = bentoSettings;

      if (window.Bento != null) {
        window.Bento.initialize();
      }

      if (window.Candu != null) {
        window.Candu.init({
          clientToken: 'J8AzreaNAI',
          userId: user.id.toString(),
          traits: {
            email: user.email,
            name: user.name,
            role: user.company_role.name,
            createdAt: user.created_at,
            permittedLocationCount: permittedLocations.length,
          },
        });
      }
    }
  }, [setupComplete, user, company, permittedLocations, functionModules]);

  useEffect(() => {
    dispatch({
      type: 'improvedSchedule/updateSelectedDate',
      payload: {
        selectedDate: moment(),
      },
    });

    const action = extractSearchParam(window.location.href, 'action');
    if (action === 'downloadReport') {
      const reportId = extractSearchParam(window.location.href, 'reportId');

      if (reportId) {
        // use the reportId to get the real download url
        dispatch({
          type: 'app/fetchCustomReportDownloadUrl',
          payload: parseInt(reportId, 10),
        }).then((res) => {
          const downloadUrl = res.download_url;
          window.location = downloadUrl;
        });
      }
    }
  }, [dispatch]);

  React.useEffect(() => {
    const {
      user_id,
      created_at: createdAt,
      email,
      uuid,
      company: { id: companyId },
    } = user;

    if (!user_id || !analytics) return;

    // ajs_user_id is set by Segment analytics
    const segmentId = window.localStorage.getItem('ajs_user_id') || '';
    if (/\D/.test(segmentId.replace(/"/g, ''))) {
      // Re-identify Segment analytics when user id is not a number
      analytics.identify(`${user_id}`, {
        company_id: companyId,
        createdAt,
        email,
        uuid,
      });
    }
  }, [location, user, analytics]);

  if (!setupComplete) {
    return <FullHeightWrapperWithLoader spinning />;
  }

  return (
    <GrowthBookProvider growthbook={window.__WS_CTX__.getGrowthbook()}>
      <S.PageLayout
        id="main_layout_wrap"
        px={isNoPaddingRoute ? 0 : { phoneSmall: 28, tablet: 34 }}
        py={isNoPaddingRoute ? 0 : 24}
      >
        <iframe title="File download iframe" id="download_iframe" style={{ display: 'none' }} />
        {children}

        <Suspense fallback={null}>
          <AuthorizedRedirects />
          <AsyncTasksWidget />
        </Suspense>
      </S.PageLayout>
    </GrowthBookProvider>
  );
}

export default AuthorizedApp;
